import type { MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { StudentCohortAssignmentReleased } from "../auth/api-client";
import { formatDueCountdown, formatDueDateShort } from "./formatDueCountdown";
import {
  deriveStudentDisplayStatus,
  studentAssignmentOpen,
  studentDisplayStatusLabel,
} from "./studentDisplayStatus";
import { LessonIcon } from "../teacher/LessonIcon";
import { QuizIcon } from "../teacher/QuizIcon";
import { isAdvancedLessonMode, lessonEpicVisualStyle } from "../teacher/lessonAdvancedVisual";
import { fallbackGradient, lessonIconDefinition } from "../teacher/lessonIcons";
import { textColorsForBackground } from "../teacher/lessonCardColors";
import { buildCohortLessonStudentLink, cohortLessonStudentLinkFromAssignment } from "./cohortLessonStudentLink";
import { useLessonLaunch } from "../transition/LessonLaunchProvider";

function AssignmentOpenBadge({ a }: { a: StudentCohortAssignmentReleased }) {
  if (!a.released) {
    return <span className="student-lesson-availability student-lesson-availability--planned">Planned</span>;
  }
  if (a.closed) {
    return <span className="student-lesson-availability student-lesson-availability--closed">Closed</span>;
  }
  return <span className="student-lesson-availability student-lesson-availability--open">Open</span>;
}

function StudentStatusBadge({ a }: { a: StudentCohortAssignmentReleased }) {
  const open = studentAssignmentOpen(a);
  const status = deriveStudentDisplayStatus(a);
  const mod = open ? "open" : "closed";
  return (
    <span className={`student-display-status student-display-status--${mod} student-display-status--${status}`}>
      {studentDisplayStatusLabel(a)}
    </span>
  );
}

export interface StudentLessonCardProps {
  readonly assignment: StudentCohortAssignmentReleased;
  readonly cohortId: string;
  readonly nowMs: number;
}

export function StudentLessonCard({ assignment: a, cohortId, nowMs }: StudentLessonCardProps) {
  const launch = useLessonLaunch();
  const isQuiz = a.itemKind === "quiz" && a.quizId;
  const itemKey = isQuiz ? a.quizId! : a.lessonId ?? a.assignmentId;
  const openUrl = cohortLessonStudentLinkFromAssignment(cohortId, a);
  const lessonOpenUrl = buildCohortLessonStudentLink({
    cohortId,
    lessonId: a.lessonId ?? "",
    lessonTitle: a.lessonTitle,
    iconName: a.iconName,
    iconBgColor: a.iconBgColor,
    iconFgColor: a.iconFgColor,
    workspaceMode: a.workspaceMode,
    reviewClosed: a.closed && a.released,
  });
  const canOpen = a.open;
  const canReviewClosed = !isQuiz && a.closed && a.released;
  const advanced = !isQuiz && isAdvancedLessonMode(a.workspaceMode);
  const epic = lessonEpicVisualStyle({
    workspaceMode: a.workspaceMode,
    baseColor: a.iconBgColor,
    fallbackSeed: itemKey,
  });
  const launchBackground = epic?.innerBackground ?? a.iconBgColor?.trim() ?? fallbackGradient(itemKey);
  const launchWatermarkColor = a.iconFgColor?.trim() || textColorsForBackground(launchBackground).title;
  const launchIcon = lessonIconDefinition(a.iconName);

  const onOpen = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isQuiz) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    launch({ to: lessonOpenUrl, background: launchBackground, watermarkColor: launchWatermarkColor, iconDef: launchIcon });
  };

  return (
    <article
      className={`student-lesson-card${advanced ? " student-lesson-card--advanced" : ""}`}
      style={epic?.frameStyle}
      title={advanced ? `${a.lessonTitle} · Advanced multi-file workspace` : undefined}
    >
      <div className="student-lesson-card-head">
        {isQuiz ? (
          <QuizIcon
            quizId={a.quizId!}
            title={a.lessonTitle}
            iconName={a.iconName}
            bgColor={a.iconBgColor}
            fgColor={a.iconFgColor}
            size={2.75}
          />
        ) : (
          <LessonIcon
            lessonId={a.lessonId ?? itemKey}
            title={a.lessonTitle}
            iconName={a.iconName}
            bgColor={a.iconBgColor}
            fgColor={a.iconFgColor}
            workspaceMode={a.workspaceMode}
            size={2.75}
          />
        )}
        <div className="student-lesson-card-title-block">
          <h3 className="student-lesson-card-title">{a.lessonTitle}</h3>
          <div className="student-lesson-card-badges">
            <AssignmentOpenBadge a={a} />
            {a.released ? <StudentStatusBadge a={a} /> : null}
          </div>
        </div>
      </div>

      {a.released ? (
        <div className="student-lesson-card-meta">
          {a.open && a.dueAt ? (
            <p className={`student-lesson-card-due${formatDueCountdown(a.dueAt, nowMs) === "Past due" ? " student-lesson-card-due--past" : ""}`}>
              Due {formatDueDateShort(a.dueAt)} · {formatDueCountdown(a.dueAt, nowMs)}
            </p>
          ) : a.dueAt ? (
            <p className="student-lesson-card-due muted small">Due {formatDueDateShort(a.dueAt)}</p>
          ) : (
            <p className="student-lesson-card-due muted small">No Due Date</p>
          )}
        </div>
      ) : null}

      <div className="student-lesson-card-action">
        {canOpen ? (
          <Link to={openUrl} onClick={onOpen} className="btn primary student-lesson-card-btn">
            {isQuiz ? "Open quiz" : "Open lesson"}
          </Link>
        ) : canReviewClosed ? (
          <Link to={openUrl} onClick={onOpen} className="btn secondary tight" style={{ textDecoration: "none" }}>
            Review
          </Link>
        ) : !a.released ? (
          <span className="muted small">Not assigned yet</span>
        ) : null}
      </div>
    </article>
  );
}
